import { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Edit2, Trash2, Ruler } from 'lucide-react';
import { unidadesService } from '@/services/productos.service';
import { satCatalogService } from '@/services/sat-catalog.service';
import { useAuthStore } from '@/stores/auth-store';
import { useApiError } from '@/hooks/useApiError';
import { dialogs } from '@/stores/dialog-store';

export function UnitsPage() {
  const { user } = useAuthStore();
  const { handleAuthRedirect } = useApiError();
  const [units, setUnits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create form
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newAbbr, setNewAbbr] = useState('');
  const [newSatUnitKey, setNewSatUnitKey] = useState('');
  const [saving, setSaving] = useState(false);

  // Edit modal
  const [editingUnit, setEditingUnit] = useState<any>(null);
  const [editName, setEditName] = useState('');
  const [editAbbr, setEditAbbr] = useState('');
  const [editSatUnitKey, setEditSatUnitKey] = useState('');
  const [editSatInputValue, setEditSatInputValue] = useState('');
  const [editSatResults, setEditSatResults] = useState<any[]>([]);
  const [editShowSatDropdown, setEditShowSatDropdown] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const editSatContainerRef = useRef<HTMLDivElement>(null);
  const editDebounceRef = useRef<any>(null);
  const editSatCatalogFullRef = useRef<any[]>([]);

  // Create SAT autocomplete
  const [satResults, setSatResults] = useState<any[]>([]);
  const [showSatDropdown, setShowSatDropdown] = useState(false);
  const [satInputValue, setSatInputValue] = useState('');
  const satContainerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<any>(null);
  const satCatalogFullRef = useRef<any[]>([]);

  const loadUnits = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await unidadesService.findAll(user?.company?.id || '');
      setUnits(data || []);
    } catch (err: any) {
      if (err?.response?.status === 401) { handleAuthRedirect(); return; }
      setError(err.response?.data?.message || 'Error al cargar unidades');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.company?.id) loadUnits();
  }, [user?.company?.id]);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current);
      clearTimeout(editDebounceRef.current);
    };
  }, []);

  // Load SAT units on mount
  useEffect(() => {
    satCatalogService.findUnits('').then(data => {
      const list = data || [];
      satCatalogFullRef.current = list;
      editSatCatalogFullRef.current = list;
      setSatResults(list);
      setEditSatResults(list);
    }).catch(() => {});
  }, []);

  // ── Create SAT search ──
  const searchSatUnits = useCallback(async (search: string) => {
    try {
      const result = await satCatalogService.findUnits(search || '');
      setSatResults(result || []);
    } catch { /* empty */ }
  }, []);

  // Close create dropdown on click outside
  useEffect(() => {
    const handleClickOutside = async (e: MouseEvent) => {
      if (satContainerRef.current && !satContainerRef.current.contains(e.target as Node)) {
        setShowSatDropdown(false);
        if (newSatUnitKey && satCatalogFullRef.current.length > 0) {
          const found = satCatalogFullRef.current.find((s: any) => s.code === newSatUnitKey);
          if (found) setSatInputValue(`${found.code} - ${found.name}`);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [newSatUnitKey]);

  // ── Edit SAT search ──
  const searchEditSatUnits = useCallback(async (search: string) => {
    try {
      const result = await satCatalogService.findUnits(search || '');
      setEditSatResults(result || []);
    } catch { /* empty */ }
  }, []);

  // Load edit SAT dropdown on mount when editing
  useEffect(() => {
    if (!editingUnit) return;
    if (editSatCatalogFullRef.current.length === 0) {
      satCatalogService.findUnits('').then(data => {
        const list = data || [];
        editSatCatalogFullRef.current = list;
        setEditSatResults(list);
      });
    }
  }, [editingUnit]);

  // Close edit dropdown on click outside
  useEffect(() => {
    const handleClickOutside = async (e: MouseEvent) => {
      if (editSatContainerRef.current && !editSatContainerRef.current.contains(e.target as Node)) {
        setEditShowSatDropdown(false);
        if (editSatUnitKey && editSatCatalogFullRef.current.length > 0) {
          const found = editSatCatalogFullRef.current.find((s: any) => s.code === editSatUnitKey);
          if (found) setEditSatInputValue(`${found.code} - ${found.name}`);
        }
      }
    };
    if (editingUnit) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [editingUnit, editSatUnitKey]);

  const openEditModal = (unit: any) => {
    setEditingUnit(unit);
    setEditName(unit.name);
    setEditAbbr(unit.abbreviation);
    setEditSatUnitKey(unit.satUnitKey || '');
    setEditSaving(false);

    // Set SAT display value
    if (unit.satUnitKey) {
      const found = editSatCatalogFullRef.current.find((s: any) => s.code === unit.satUnitKey);
      setEditSatInputValue(found ? `${found.code} - ${found.name}` : unit.satUnitKey);
    } else {
      setEditSatInputValue('');
    }
  };

  const handleEditSubmit = async () => {
    if (!editingUnit || !editName.trim() || !editAbbr.trim()) return;
    setEditSaving(true);
    try {
      await unidadesService.update(editingUnit.id, {
        name: editName.trim(),
        abbreviation: editAbbr.trim(),
        satUnitKey: editSatUnitKey || undefined,
      });
      setEditingUnit(null);
      loadUnits();
    } catch {
      await dialogs.alert('Error al actualizar unidad');
    } finally {
      setEditSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim() || !newAbbr.trim()) return;
    if (!user?.company?.id) return;
    setSaving(true);
    try {
      await unidadesService.create({
        companyId: user.company.id,
        name: newName.trim(),
        abbreviation: newAbbr.trim(),
        satUnitKey: newSatUnitKey || undefined,
      });
      setNewName('');
      setNewAbbr('');
      setNewSatUnitKey('');
      setShowForm(false);
      loadUnits();
    } catch {
      await dialogs.alert('Error al crear unidad');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!await dialogs.confirm(`¿Desactivar unidad "${name}"?`)) return;
    try {
      await unidadesService.deactivate(id);
      loadUnits();
    } catch {
      await dialogs.alert('Error al desactivar unidad');
    }
  };

  return (
    <div className="w-full space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Unidades de Medida</h1>
          <p className="text-sm text-ios-gray mt-1">Define las unidades para tus productos</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="ios-button flex items-center gap-2">
          <Plus size={18} />
          Nueva Unidad
        </button>
      </div>

      {showForm && (
        <div className="ios-card p-4 space-y-3">
          <div className="flex gap-3">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Nombre (ej: Kilogramo)..."
              className="ios-input flex-1"
              autoFocus
            />
            <input
              type="text"
              value={newAbbr}
              onChange={(e) => setNewAbbr(e.target.value)}
              placeholder="Abrev. (ej: kg)..."
              className="ios-input w-24"
            />
            <div ref={satContainerRef} className="relative flex-1">
              <input
                type="text"
                value={satInputValue}
                onChange={(e) => {
                  const val = e.target.value;
                  setSatInputValue(val);
                  setShowSatDropdown(true);
                  setNewSatUnitKey('');
                  clearTimeout(debounceRef.current);
                  debounceRef.current = setTimeout(() => searchSatUnits(val), 200);
                }}
                onFocus={() => {
                  setShowSatDropdown(true);
                  if (satResults.length === 0) searchSatUnits('');
                }}
                onKeyDown={(e) => { if (e.key === 'Escape') setShowSatDropdown(false); }}
                className="ios-input w-full pr-8 text-sm"
                placeholder="Busca unidad SAT (ej: pieza, kilo)..."
                autoComplete="off"
              />
              {newSatUnitKey && satInputValue && (
                <button
                  type="button"
                  onClick={() => { setNewSatUnitKey(''); setSatInputValue(''); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-ios-gray hover:text-ios-red text-lg leading-none"
                >
                  ×
                </button>
              )}
              {showSatDropdown && (
                <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-ios-gray-5 rounded-[12px] shadow-ios-lg max-h-48 overflow-y-auto">
                  {satResults.length === 0 ? (
                    <div className="text-center py-4 text-ios-gray text-xs">Sin resultados</div>
                  ) : (
                    satResults.map((item: any) => (
                      <button
                        key={item.code}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setNewSatUnitKey(item.code);
                          setSatInputValue(`${item.code} - ${item.name}`);
                          setShowSatDropdown(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-ios-blue/5 transition-colors border-b border-ios-gray-5 last:border-b-0 ${
                          newSatUnitKey === item.code ? 'bg-ios-blue/10 text-ios-blue font-medium' : ''
                        }`}
                      >
                        <code className="font-mono bg-ios-gray-6 px-1 py-0.5 rounded mr-1.5">{item.code}</code>
                        <span>{item.name}</span>
                        {item.symbol && <span className="text-ios-gray ml-1">({item.symbol})</span>}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleCreate} disabled={saving || !newName.trim() || !newAbbr.trim()} className="ios-button">
              {saving ? 'Guardando...' : 'Crear'}
            </button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-ios-gray hover:text-foreground">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 text-ios-red text-sm p-4 rounded-[12px] border border-red-100">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-6 h-6 border-2 border-ios-blue/30 border-t-ios-blue rounded-full animate-spin" />
        </div>
      ) : units.length === 0 ? (
        <div className="ios-card p-12 text-center">
          <Ruler size={48} className="mx-auto text-ios-gray-4 mb-4" />
          <p className="text-sm text-ios-gray">No hay unidades registradas</p>
        </div>
      ) : (
        <div className="ios-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-ios-gray-5 bg-ios-gray-6/50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-ios-gray uppercase">Nombre</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-ios-gray uppercase">Abreviatura</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-ios-gray uppercase">Clave SAT Unidad</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-ios-gray uppercase">Productos</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-ios-gray uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ios-gray-5">
              {units.map((unit: any) => {
                const satUnit = satResults.find((s: any) => s.code === unit.satUnitKey);
                return (
                  <tr key={unit.id} className="hover:bg-ios-gray-6/30 transition-colors">
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-ios-purple/10 rounded-[10px] flex items-center justify-center">
                          <Ruler size={16} className="text-ios-purple" />
                        </div>
                        <span className="text-sm font-medium">{unit.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <code className="text-xs bg-ios-gray-6 px-2 py-1 rounded-md font-mono">{unit.abbreviation}</code>
                    </td>
                    <td className="px-4 py-3.5">
                      {unit.satUnitKey ? (
                        <span className="text-xs" title={satUnit?.name}>
                          <code className="bg-ios-blue/10 text-ios-blue px-2 py-0.5 rounded-md font-mono">{unit.satUnitKey}</code>
                          {satUnit && <span className="text-ios-gray ml-1.5">{satUnit.name}</span>}
                        </span>
                      ) : (
                        <span className="text-xs text-ios-gray">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-right text-sm">{unit._count?.products || 0}</td>
                    <td className="px-4 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEditModal(unit)}
                          className="p-1.5 rounded-lg hover:bg-blue-50 text-ios-gray hover:text-ios-blue transition-colors"
                          title="Editar"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(unit.id, unit.name)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-ios-gray hover:text-ios-red transition-colors"
                          title="Desactivar"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Edit Modal ── */}
      {editingUnit && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setEditingUnit(null)}>
          <div className="bg-white rounded-[20px] w-full max-w-lg shadow-ios-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-ios-gray-5">
              <h3 className="font-semibold">Editar Unidad</h3>
              <button onClick={() => setEditingUnit(null)} className="p-2 rounded-lg hover:bg-ios-gray-6">
                <span className="text-lg leading-none">×</span>
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-xs text-ios-gray mb-1 block font-medium">Nombre *</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="ej: Kilogramo"
                  className="ios-input w-full"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs text-ios-gray mb-1 block font-medium">Abreviatura *</label>
                <input
                  type="text"
                  value={editAbbr}
                  onChange={(e) => setEditAbbr(e.target.value)}
                  placeholder="ej: kg"
                  className="ios-input w-full"
                />
              </div>
              <div ref={editSatContainerRef}>
                <label className="text-xs text-ios-gray mb-1 block font-medium">Clave SAT Unidad</label>
                <div className="relative">
                  <input
                    type="text"
                    value={editSatInputValue}
                    onChange={(e) => {
                      const val = e.target.value;
                      setEditSatInputValue(val);
                      setEditShowSatDropdown(true);
                      setEditSatUnitKey('');
                      clearTimeout(editDebounceRef.current);
                      editDebounceRef.current = setTimeout(() => searchEditSatUnits(val), 200);
                    }}
                    onFocus={() => {
                      setEditShowSatDropdown(true);
                      if (editSatResults.length === 0) searchEditSatUnits('');
                    }}
                    onKeyDown={(e) => { if (e.key === 'Escape') setEditShowSatDropdown(false); }}
                    className="ios-input w-full pr-8 text-sm"
                    placeholder="Busca unidad SAT..."
                    autoComplete="off"
                  />
                  {editSatUnitKey && editSatInputValue && (
                    <button
                      type="button"
                      onClick={() => { setEditSatUnitKey(''); setEditSatInputValue(''); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-ios-gray hover:text-ios-red text-lg leading-none"
                    >
                      ×
                    </button>
                  )}
                  {editShowSatDropdown && (
                    <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-ios-gray-5 rounded-[12px] shadow-ios-lg max-h-48 overflow-y-auto">
                      {editSatResults.length === 0 ? (
                        <div className="text-center py-4 text-ios-gray text-xs">Sin resultados</div>
                      ) : (
                        editSatResults.map((item: any) => (
                          <button
                            key={item.code}
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setEditSatUnitKey(item.code);
                              setEditSatInputValue(`${item.code} - ${item.name}`);
                              setEditShowSatDropdown(false);
                            }}
                            className={`w-full text-left px-3 py-2 text-xs hover:bg-ios-blue/5 transition-colors border-b border-ios-gray-5 last:border-b-0 ${
                              editSatUnitKey === item.code ? 'bg-ios-blue/10 text-ios-blue font-medium' : ''
                            }`}
                          >
                            <code className="font-mono bg-ios-gray-6 px-1 py-0.5 rounded mr-1.5">{item.code}</code>
                            <span>{item.name}</span>
                            {item.symbol && <span className="text-ios-gray ml-1">({item.symbol})</span>}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-ios-gray-5 flex gap-2 justify-end">
              <button
                onClick={() => setEditingUnit(null)}
                className="px-4 py-2.5 rounded-[10px] text-sm border border-ios-gray-5 hover:bg-ios-gray-6"
              >
                Cancelar
              </button>
              <button
                onClick={handleEditSubmit}
                disabled={editSaving || !editName.trim() || !editAbbr.trim()}
                className="px-4 py-2.5 rounded-[10px] text-sm bg-ios-blue text-white font-medium hover:bg-ios-blue-600 disabled:opacity-50"
              >
                {editSaving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
